/** Enregistrement live : webcam + plateau en un seul flux (pas de fusion après). */
export const RECORD_COMPOSITE_LIVE = true;

/** Caméra 720p 16:9 */
export const CAMERA_CONSTRAINTS = {
  video: {
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    aspectRatio: { ideal: 16 / 9 },
    frameRate: { ideal: 30, max: 30 },
    facingMode: "user",
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
  },
};

/** Vidéo caméra seule (legacy / tests). */
export const RECORDER_BITRATES = {
  videoBitsPerSecond: 450_000,
  audioBitsPerSecond: 48_000,
};

/** Vue plateau 1280×720 live. */
export const COMPOSITE_RECORDER_BITRATES = {
  videoBitsPerSecond: 2_500_000,
  audioBitsPerSecond: 96_000,
};

/** MP4 en priorité (seek natif, lecture admin) ; WebM en repli (Firefox, etc.). */
const SHARED_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4;codecs=vp9,opus",
  "video/mp4;codecs=vp8,opus",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

const COMPOSITE_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4;codecs=vp9,opus",
  "video/mp4;codecs=vp8,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm",
];

export function pickRecorderMimeType({ forComposite = false } = {}) {
  const list = forComposite ? COMPOSITE_MIME_CANDIDATES : SHARED_MIME_CANDIDATES;
  if (typeof MediaRecorder === "undefined") return "video/webm";
  return list.find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
}

export function createMediaRecorder(stream, bitrates = RECORDER_BITRATES, options = {}) {
  const mimeType = pickRecorderMimeType(options);
  const withBitrates = { mimeType, ...bitrates };

  try {
    const recorder = new MediaRecorder(stream, withBitrates);
    return { recorder, mimeType: recorder.mimeType || mimeType };
  } catch {
    try {
      const recorder = new MediaRecorder(stream, { mimeType });
      return { recorder, mimeType: recorder.mimeType || mimeType };
    } catch {
      const recorder = new MediaRecorder(stream);
      return { recorder, mimeType: recorder.mimeType || mimeType };
    }
  }
}

const MIX_AUDIO_MIME_CANDIDATES = [
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm",
];

/** MediaRecorder avec mix Web Audio — WebM d'abord (MP4 + piste mixée échoue souvent). */
export function createMixedCompositeRecorder(stream, bitrates = COMPOSITE_RECORDER_BITRATES) {
  if (typeof MediaRecorder === "undefined") {
    return createMediaRecorder(stream, bitrates, { forComposite: true });
  }
  for (const mimeType of MIX_AUDIO_MIME_CANDIDATES) {
    if (!MediaRecorder.isTypeSupported(mimeType)) continue;
    try {
      const recorder = new MediaRecorder(stream, { mimeType, ...bitrates });
      return { recorder, mimeType: recorder.mimeType || mimeType };
    } catch {
      /* essai suivant */
    }
  }
  return createMediaRecorder(stream, bitrates, { forComposite: true });
}
